import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Spin, Tag, Divider, Alert } from 'antd'
import {
  DollarOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'
import { getComprehensiveBalance } from '../../backend-integration/api'
import { getPatientLedgerSummary } from '../../backend-integration/api'

/**
 * Component to display comprehensive account balance
 * @param {string} patientId - Patient ID to display balance for
 */
const ComprehensiveBalance = ({ patientId }) => {
  const [loading, setLoading] = useState(true)
  const [balanceData, setBalanceData] = useState(null)
  const [ledgerSummary, setLedgerSummary] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadBalanceData()
  }, [patientId])

  const loadBalanceData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load both comprehensive balance and ledger summary
      const [balance, summary] = await Promise.all([
        getComprehensiveBalance(patientId),
        getPatientLedgerSummary(patientId)
      ])

      setBalanceData(balance)
      setLedgerSummary(summary)
    } catch (err) {
      console.error('Error loading balance data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size='large' />
        </div>
      </Card>
    )
  }

  if (error) {
    return <Alert message='Error Loading Balance' description={error} type='error' showIcon />
  }

  const formatCurrency = (amount) => {
    return `R ${Math.abs(amount).toFixed(2)}`
  }

  const getBalanceColor = (amount) => {
    if (amount > 0) return '#f5222d' // Red for owing
    if (amount < 0) return '#52c41a' // Green for credit
    return '#1890ff' // Blue for zero
  }

  return (
    <Card title='Comprehensive Account Balance' bordered={false}>
      {/* Main Balance Display */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Statistic
              title='Current Balance'
              value={formatCurrency(balanceData?.summary?.netBalance || 0)}
              valueStyle={{
                color: getBalanceColor(balanceData?.summary?.netBalance || 0),
                fontSize: '32px'
              }}
              prefix={<DollarOutlined />}
              suffix={
                balanceData?.summary?.netBalance > 0 ? (
                  <Tag color='red'>Amount Owed</Tag>
                ) : balanceData?.summary?.netBalance < 0 ? (
                  <Tag color='green'>Credit Balance</Tag>
                ) : (
                  <Tag color='blue'>Settled</Tag>
                )
              }
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Transaction Summary */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title='Total Invoiced'
              value={formatCurrency(balanceData?.summary?.totalInvoiced || 0)}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title='Patient Payments'
              value={formatCurrency(balanceData?.summary?.totalPatientPayments || 0)}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title='Medical Aid Payments'
              value={formatCurrency(balanceData?.summary?.totalMedicalAidPayments || 0)}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Separated Balances */}
      {ledgerSummary && (
        <>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Card title='Medical Aid Balance' size='small'>
                <Statistic
                  value={formatCurrency(ledgerSummary.medicalAidBalance || 0)}
                  valueStyle={{
                    color: getBalanceColor(ledgerSummary.medicalAidBalance || 0)
                  }}
                  suffix={
                    ledgerSummary.medicalAidBalance > 0 ? (
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />
                    ) : (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    )
                  }
                />
              </Card>
            </Col>

            <Col xs={24} sm={12}>
              <Card title='Patient Balance' size='small'>
                <Statistic
                  value={formatCurrency(ledgerSummary.patientBalance || 0)}
                  valueStyle={{
                    color: getBalanceColor(ledgerSummary.patientBalance || 0)
                  }}
                  suffix={
                    ledgerSummary.patientBalance > 0 ? (
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />
                    ) : (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    )
                  }
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Adjustments Summary */}
      {(balanceData?.summary?.totalCredits > 0 ||
        balanceData?.summary?.totalDebits > 0 ||
        balanceData?.summary?.totalRefunds > 0 ||
        balanceData?.summary?.totalWriteOffs > 0) && (
        <>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <h4>Adjustments & Other Transactions</h4>
            </Col>
            {balanceData?.summary?.totalCredits > 0 && (
              <Col xs={12} sm={6}>
                <Statistic
                  title='Credits'
                  value={formatCurrency(balanceData.summary.totalCredits)}
                  valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                />
              </Col>
            )}
            {balanceData?.summary?.totalDebits > 0 && (
              <Col xs={12} sm={6}>
                <Statistic
                  title='Debits'
                  value={formatCurrency(balanceData.summary.totalDebits)}
                  valueStyle={{ fontSize: '16px', color: '#f5222d' }}
                />
              </Col>
            )}
            {balanceData?.summary?.totalRefunds > 0 && (
              <Col xs={12} sm={6}>
                <Statistic
                  title='Refunds'
                  value={formatCurrency(balanceData.summary.totalRefunds)}
                  valueStyle={{ fontSize: '16px', color: '#fa8c16' }}
                />
              </Col>
            )}
            {balanceData?.summary?.totalWriteOffs > 0 && (
              <Col xs={12} sm={6}>
                <Statistic
                  title='Write-offs'
                  value={formatCurrency(balanceData.summary.totalWriteOffs)}
                  valueStyle={{ fontSize: '16px', color: '#8c8c8c' }}
                />
              </Col>
            )}
          </Row>
        </>
      )}

      {/* Transaction Count */}
      <Divider />
      <Row>
        <Col span={24}>
          <Tag color='default'>Total Transactions: {ledgerSummary?.transactionCount || 0}</Tag>
          {ledgerSummary?.lastTransactionDate && (
            <Tag color='default'>
              Last Transaction: {new Date(ledgerSummary.lastTransactionDate).toLocaleDateString()}
            </Tag>
          )}
        </Col>
      </Row>
    </Card>
  )
}

export default ComprehensiveBalance
